import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { get, getDatabase, ref as dbRef, onValue, update, remove } from "firebase/database";
import { getAuth } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import { INSTANCE_TIER_OPTIONS, resolveInstanceCap } from '../utils/multiInstanceTiers';
import { CREDIT_PLANS, resolveMembershipPlanId } from '../config/creditPlans';
import {
    RESEARCH_FEATURES,
    RESEARCH_FEATURE_KEYS,
    RESEARCH_PLAN_KEYS,
    researchPlanDefault,
    resolveResearchFlag,
} from '../config/researchFeatures';
import { OUTREACH_MODES, effectiveOutreachModes } from '../config/outreachFeatures';
import './TeamDashboard.css';

// Alle Produkt-Flags an einem Ort. Die Tabelle rendert daraus ein Chip-Raster
// mit fester Reihenfolge — dadurch sitzt jedes Feature in jeder Zeile an der
// gleichen Position und lässt sich spaltenweise scannen.
const FEATURE_TOGGLES = [
    { key: 'hasDealWizard', label: 'Wizard', title: 'Deal Wizard', icon: 'fa-hat-wizard', tone: 'emerald' },
    { key: 'hasAcademy', label: 'Academy', title: 'Academy', icon: 'fa-graduation-cap', tone: 'blue' },
    { key: 'hasSalesPages', label: 'Pages', title: 'SalesPages', icon: 'fa-file-lines', tone: 'amber' },
    { key: 'hasXRAAY', label: 'XRAAY', title: 'XRAAY', icon: 'fa-radiation', tone: 'violet' },
    { key: 'hasAutoSendButton', label: 'Send+', title: 'AI Send+ Button', icon: 'fa-paper-plane', tone: 'cyan' }
];

// Outreach hängt nicht am Master-Chip, sondern an den zwei Modi. Der Filter
// kennt das Flag trotzdem, weil hasOutreachJobs gesetzt ist sobald mindestens
// ein Modus an ist.
const OUTREACH_FILTER = { key: 'hasOutreachJobs', title: 'Outreach Jobs' };

// Research-Tools der World-DB. Eigene Sektion, weil sie zusammen verkauft und
// meist gemeinsam freigeschaltet werden — Definition in config/researchFeatures.
const RESEARCH_TOGGLES = RESEARCH_FEATURES.map((f) => ({
    key: f.key,
    label: f.short,
    title: f.name,
    icon: f.icon,
    tone: f.tone
}));

// Für Filter und Normalisierung zählt beides als Produkt-Flag.
const ALL_TOGGLES = [...FEATURE_TOGGLES, OUTREACH_FILTER, ...RESEARCH_TOGGLES];

// Kurzlabels für Member-Berechtigungen (Langtexte: AVAILABLE_FEATURES in TeamContext).
const MEMBER_FEATURE_LABELS = {
    leads: 'Leads',
    inbox: 'Inbox',
    offers: 'Angebote',
    settings: 'Settings',
    autoSendButton: 'AI Send+',
    all: 'Vollzugriff'
};

const ACADEMY_TIERS = [
    { key: 'team', label: 'Team', title: 'Team Tier' },
    { key: 'standard', label: 'Standard', title: 'Standard Tier' },
    { key: 'premium', label: 'Premium', title: 'Premium Tier' }
];

// "Last Seen" kommt aus Users/{uid}/lastSeen — geschrieben vom Presence-Heartbeat
// (recordActivityHeartbeat, alle ~30s solange jemand aktiv im Tab ist).
// Accounts von vor dem Heartbeat fallen auf lastAccess/lastLogin zurück.
const MINUTE_MS = 60 * 1000;
const DAY_MS = 24 * 60 * MINUTE_MS;
const ONLINE_MS = 3 * MINUTE_MS;
const RECENT_MS = 30 * MINUTE_MS;

function toMillis(value) {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
}

function resolveLastSeen(userData) {
    return Math.max(
        toMillis(userData.lastSeen),
        toMillis(userData.lastAccess),
        toMillis(userData.lastLogin)
    );
}

function lastSeenStatus(ms, now) {
    if (!ms) return 'never';
    const diff = now - ms;
    if (diff < ONLINE_MS) return 'online';
    if (diff < RECENT_MS) return 'recent';
    if (diff < DAY_MS) return 'today';
    if (diff < 7 * DAY_MS) return 'week';
    if (diff < 30 * DAY_MS) return 'month';
    return 'stale';
}

function formatRelative(ms, now) {
    if (!ms) return 'Nie';
    const diff = Math.max(0, now - ms);
    if (diff < MINUTE_MS) return 'gerade eben';
    const minutes = Math.floor(diff / MINUTE_MS);
    if (minutes < 60) return `vor ${minutes} Min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `vor ${hours} Std`;
    const days = Math.floor(hours / 24);
    if (days < 31) return `vor ${days} ${days === 1 ? 'Tag' : 'Tagen'}`;
    const months = Math.floor(days / 30);
    if (months < 12) return `vor ${months} Mon`;
    return `vor ${Math.floor(days / 365)} J`;
}

function formatSeenLabel(ms, now) {
    if (!ms) return 'Noch nie gesehen';
    const relative = formatRelative(ms, now);
    return relative === 'gerade eben' ? 'Gerade aktiv' : `Zuletzt gesehen ${relative}`;
}

function formatAbsolute(ms) {
    if (!ms) return '—';
    return new Date(ms).toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
}

function formatShortDate(ms) {
    if (!ms) return '—';
    return new Date(ms).toLocaleDateString('de-DE', {
        day: '2-digit', month: '2-digit', year: '2-digit'
    });
}

// Sortierbare Spalten. `numeric` steuert nur die Default-Richtung beim ersten Klick.
const SORT_COLUMNS = {
    account: { accessor: (u) => (u.email || '').toLowerCase(), numeric: false },
    lastSeen: { accessor: (u) => u.lastSeenAt, numeric: true },
    credits: { accessor: (u) => u.credits || 0, numeric: true },
    pagesCount: { accessor: (u) => u.pagesCount || 0, numeric: true },
    sessionCount: { accessor: (u) => u.sessionCount || 0, numeric: true },
    photoCount: { accessor: (u) => u.photoCount || 0, numeric: true },
    signup: { accessor: (u) => toMillis(u.signupTimestamp), numeric: true }
};

// Schnellfilter der Toolbar. `test` bekommt den User plus den Abstand zum
// letzten Lebenszeichen in ms.
const STATUS_FILTERS = [
    { key: 'all', label: 'Alle', test: () => true },
    { key: 'online', label: 'Gerade aktiv', test: (u, idle) => idle < RECENT_MS },
    { key: 'today', label: 'Heute aktiv', test: (u, idle) => idle < DAY_MS },
    { key: 'week', label: '7 Tage', test: (u, idle) => idle < 7 * DAY_MS },
    { key: 'dormant', label: '30+ Tage inaktiv', test: (u, idle) => idle >= 30 * DAY_MS },
    { key: 'owner', label: 'Owner', test: (u) => u.accountType !== 'member' },
    { key: 'member', label: 'Members', test: (u) => u.accountType === 'member' },
    { key: 'premium', label: 'Membership', test: (u) => !!u.membershipPlanId }
];

const STATUS_FILTER_MAP = STATUS_FILTERS.reduce((acc, f) => {
    acc[f.key] = f;
    return acc;
}, {});

// Zählwerte liegen in eigenen Knoten (ein Request pro Account) und sind damit der
// teure Teil des Ladens. Sie ändern sich selten, werden also getrennt vom
// Users-Listener geladen — sonst würde jeder Heartbeat alles neu ziehen.
const STATS_CONCURRENCY = 10;
const EMPTY_STATS = { photoCount: null, pagesCount: null, credits: null, spyCredits: null, bonusCredits: null };

async function fetchAccountStats(db, uid) {
    const [surveys, pages, credits, spy] = await Promise.all([
        get(dbRef(db, `surveys/${uid}`)).catch(() => null),
        get(dbRef(db, `pages/${uid}`)).catch(() => null),
        get(dbRef(db, `Credits/${uid}/amount`)).catch(() => null),
        get(dbRef(db, `World/${uid}/credits`)).catch(() => null)
    ]);

    let photoCount = 0;
    const surveysData = surveys?.val();
    if (surveysData) {
        for (const survey of Object.values(surveysData)) {
            if (survey?.uploadedThumbnails) {
                photoCount += Object.keys(survey.uploadedThumbnails).length;
            }
        }
    }
    const pagesData = pages?.val();

    return {
        photoCount,
        pagesCount: pagesData ? Object.keys(pagesData).length : 0,
        credits: credits?.val() || 0,
        spyCredits: Number(spy?.val()?.spyCredits) || 0,
        bonusCredits: Number(spy?.val()?.bonusCredits) || 0
    };
}

// Blockweise abarbeiten — nicht alle 80+ Accounts gleichzeitig, aber auch nicht
// einzeln nacheinander.
async function runBatched(items, size, worker) {
    for (let i = 0; i < items.length; i += size) {
        await Promise.all(items.slice(i, i + size).map(worker));
    }
}

// Rohen Users-Record in die Form bringen, die die Tabelle erwartet.
function normalizeUser(raw, allUsers) {
    const teamMembership = raw.teamMembership || null;
    let teamOwnerEmail = null;
    if (teamMembership?.teamId && allUsers[teamMembership.teamId]) {
        teamOwnerEmail = allUsers[teamMembership.teamId].email;
    }

    // Research-Flags sind alle strikt boolesch — fehlt der Key, ist das Tool aus.
    const researchFlags = {};
    for (const key of RESEARCH_FEATURE_KEYS) {
        researchFlags[key] = raw[key] === true;
    }

    // Plan-based features: keep "not set" apart from false, so the row can fall
    // back to the membership plan (resolved in `rows`).
    const researchRaw = {};
    for (const key of RESEARCH_PLAN_KEYS) {
        researchRaw[key] = typeof raw[key] === 'boolean' ? raw[key] : null;
    }

    return {
        ...raw,
        ...researchFlags,
        researchRaw,
        sessionCount: raw.sessionCount || 0,
        lastLoginAt: toMillis(raw.lastLogin),
        lastAccessAt: toMillis(raw.lastAccess),
        lastSeenAt: resolveLastSeen(raw),
        hasSalesPages: raw.hasSalesPages || false,
        hasSalesGod: raw.hasSalesGod || false,
        hasXRAAY: raw.hasXRAAY || false,
        hasAutoSendButton: raw.hasAutoSendButton === true,
        hasOutreachJobs: raw.hasOutreachJobs === true,
        // Ohne Master-Flag sind beide Modi aus. Nur wenn Outreach an ist, gilt
        // der Legacy-Fallback (fehlender Knoten = nur Auto Jobs).
        outreachModes: effectiveOutreachModes(raw.hasOutreachJobs === true, raw.outreachModes),
        // Kein Produkt-Flag, sondern ein Kill-Switch: die Inbox läuft von selbst,
        // sobald ein Mail-Konto verbunden ist, und nur dieses Flag stoppt sie.
        inboxDisabled: raw.inboxDisabled === true,
        academyTiers: raw.academyTiers || {},
        multiInstance: raw.multiInstance === true,
        multiInstanceTier: raw.multiInstanceTier || null,
        multiInstanceCap: resolveInstanceCap(raw),
        parentOwnerUid: raw.parentOwnerUid || null,
        emailPlan: raw.emailPlan || 'standard',
        membershipPlan: raw.membershipPlan || null,
        dailyEmailLimit: raw.dailyEmailLimit || 45,
        accountType: raw.accountType || 'owner',
        teamMembership,
        teamOwnerEmail
    };
}


function PremiumToggle() {
    const [users, setUsers] = useState({});
    const [stats, setStats] = useState({});
    const [statsLoading, setStatsLoading] = useState(false);
    const statsRef = useRef({});
    const claimedRef = useRef(new Set());
    const [showCreditsModal, setShowCreditsModal] = useState(false);
    const [selectedUserForCredits, setSelectedUserForCredits] = useState(null);
    const [creditsToAdd, setCreditsToAdd] = useState('');
    const [isAddingCredits, setIsAddingCredits] = useState(false);
    const db = getDatabase();
    const auth = getAuth();
    const currentUser = auth.currentUser;
    const [showModal, setShowModal] = useState(false);
    const [currentThumbnails, setCurrentThumbnails] = useState([]);
    const [sortKey, setSortKey] = useState('lastSeen');
    const [sortDir, setSortDir] = useState('desc');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [featureFilter, setFeatureFilter] = useState('all');
    const [copiedUid, setCopiedUid] = useState(null);
    const [userPlans, setUserPlans] = useState({});
    const [plansLoading, setPlansLoading] = useState(false);
    const [expandedTeams, setExpandedTeams] = useState(() => new Set());
    // Tickt jede Minute, damit "vor X Min" und der Online-Punkt live bleiben.
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), MINUTE_MS);
        return () => clearInterval(timer);
    }, []);

    // Live-Listener auf Users. Bewusst synchron: der Callback feuert bei jedem
    // Heartbeat-Update und darf deshalb keine Folge-Requests auslösen.
    useEffect(() => {
        if (!currentUser) return undefined;

        const unsubscribe = onValue(dbRef(db, 'Users'), (snapshot) => {
            const usersData = snapshot.val() || {};
            const mapped = {};
            for (const [userId, raw] of Object.entries(usersData)) {
                mapped[userId] = normalizeUser(raw, usersData);
            }
            setUsers(mapped);
        });

        return () => unsubscribe();
    }, [db, currentUser]);

    // Same loader as the Memberships tab. Do not depend on the Users object
    // itself — lastSeen heartbeats would restart and cancel the fetch.
    const userIdsKey = useMemo(() => Object.keys(users).sort().join(','), [users]);

    useEffect(() => {
        if (!currentUser || !userIdsKey) return undefined;
        const ids = userIdsKey.split(',');
        let cancelled = false;
        setPlansLoading(true);
        const getAllCreditsAdminFn = httpsCallable(functions, 'getAllCreditsAdmin');
        (async () => {
            try {
                const result = await getAllCreditsAdminFn({ userIds: ids });
                if (!cancelled) setUserPlans(result.data.plans || {});
            } catch (err) {
                console.warn('[Members] Failed to load memberships:', err?.message || err);
                if (!cancelled) setUserPlans({});
            } finally {
                if (!cancelled) setPlansLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [userIdsKey, currentUser]);

    // Zählwerte nachladen — nur für Accounts, die noch keine haben.
    const loadStats = useCallback(async (userIds, force = false) => {
        if (force) claimedRef.current = new Set();
        const todo = userIds.filter((uid) => !claimedRef.current.has(uid));
        if (todo.length === 0) return;
        todo.forEach((uid) => claimedRef.current.add(uid));

        setStatsLoading(true);
        try {
            await runBatched(todo, STATS_CONCURRENCY, async (uid) => {
                statsRef.current[uid] = await fetchAccountStats(db, uid);
            });
            setStats({ ...statsRef.current });
        } finally {
            setStatsLoading(false);
        }
    }, [db]);

    useEffect(() => {
        const userIds = Object.keys(users);
        if (userIds.length > 0) loadStats(userIds);
    }, [users, loadStats]);

    // Add spy credits to user via admin Cloud Function
    const handleAddSpyCredits = async () => {
        if (!selectedUserForCredits || !creditsToAdd) return;
        
        const amount = parseFloat(creditsToAdd);
        if (isNaN(amount) || amount <= 0) return;

        setIsAddingCredits(true);
        try {
            const addCreditsFn = httpsCallable(functions, 'addCreditsAdmin');
            const result = await addCreditsFn({
                targetUserId: selectedUserForCredits.id,
                amount,
            });

            statsRef.current[selectedUserForCredits.id] = {
                ...(statsRef.current[selectedUserForCredits.id] || EMPTY_STATS),
                spyCredits: result.data.monthlyBalance ?? (statsRef.current[selectedUserForCredits.id]?.spyCredits || 0),
                bonusCredits: result.data.bonusBalance,
            };
            setStats({ ...statsRef.current });
            
            setShowCreditsModal(false);
            setSelectedUserForCredits(null);
            setCreditsToAdd('');
        } catch (error) {
            console.error('Error adding spy credits:', error);
            alert(`Failed to add credits: ${error.message}`);
        } finally {
            setIsAddingCredits(false);
        }
    };

    // Open credits modal
    const openSpyCreditsModal = (userId, userData) => {
        setSelectedUserForCredits({ id: userId, email: userData.email, name: userData.name });
        setCreditsToAdd('');
        setShowCreditsModal(true);
    };

    // Ein Handler für alle Produkt-Flags aus FEATURE_TOGGLES.
    const toggleFeature = (userId, featureKey) => {
        const userRef = dbRef(db, `Users/${userId}`);
        const isOn = users[userId]?.[featureKey] === true;
        const updates = { [featureKey]: !isOn };
        // Academy startet ohne Tier — die Flags müssen beim Aktivieren existieren.
        if (featureKey === 'hasAcademy' && !isOn) {
            updates.academyTiers = { team: false, standard: false, premium: false };
        }
        update(userRef, updates).catch(error => {
            console.error(`Error updating ${featureKey}:`, error);
        });
    };

    // Inbox-Abruf: kein Produkt-Flag, sondern der einzige Weg, die Inbox
    // abzuschalten. Läuft über eine Callable, weil dabei auch der aufgelöste
    // Zustand in /World/{uid}/settings neu geschrieben werden muss.
    const toggleInboxFetching = async (userId) => {
        const enabled = users[userId]?.inboxDisabled === true;
        try {
            await httpsCallable(functions, 'setInboxFetchingAdmin')({ userId, enabled });
        } catch (error) {
            console.error('Error updating inbox fetching:', error);
            alert(`Inbox-Abruf konnte nicht umgestellt werden: ${error.message}`);
        }
    };

    // Research wird in der Praxis meist als Paket freigeschaltet. Der Schalter
    // setzt alles auf denselben Stand: an, sobald irgendetwas noch fehlt.
    // Plan-based features store null when the wanted state equals the plan
    // default, so they keep following the plan after an upgrade/downgrade.
    const researchValue = (row, key, on) => (
        RESEARCH_PLAN_KEYS.includes(key) && on === researchPlanDefault(key, row?.membershipPlanId) ? null : on
    );

    const toggleResearch = (userId, key) => {
        const row = rows[userId] || {};
        update(dbRef(db, `Users/${userId}`), { [key]: researchValue(row, key, row[key] !== true) }).catch(error => {
            console.error(`Error updating ${key}:`, error);
        });
    };

    const toggleAllResearch = (userId) => {
        const row = rows[userId] || {};
        const allOn = RESEARCH_FEATURE_KEYS.every((key) => row[key] === true);
        const updates = {};
        for (const key of RESEARCH_FEATURE_KEYS) {
            updates[key] = researchValue(row, key, !allOn);
        }
        update(dbRef(db, `Users/${userId}`), updates).catch(error => {
            console.error('Error updating research features:', error);
        });
    };

    // Die zwei Modi sind unabhängig: beide, nur Prepare, nur Auto Jobs, oder
    // keines. hasOutreachJobs folgt daraus — an, sobald mindestens ein Modus an
    // ist. Der Knoten wird immer vollständig geschrieben, sonst fällt ein
    // fehlender Key in den Legacy-Default (nur Auto Jobs).
    const toggleOutreachMode = (userId, modeId) => {
        const user = users[userId] || {};
        const current = effectiveOutreachModes(user.hasOutreachJobs === true, user.outreachModes);
        const next = { ...current, [modeId]: !current[modeId] };
        const enabled = next.send === true || next.prepare === true;
        update(dbRef(db, `Users/${userId}`), {
            hasOutreachJobs: enabled,
            outreachModes: next,
        }).catch(error => {
            console.error(`Error updating outreach mode ${modeId}:`, error);
        });
    };

    const toggleAcademyTier = (userId, tier) => {
        const userRef = dbRef(db, `Users/${userId}/academyTiers`);
        const currentTiers = users[userId].academyTiers || {};
        update(userRef, { [tier]: !currentTiers[tier] }).catch(error => {
            console.error("Error updating academyTier:", error);
        });
    };

    // Multi-Instance Feature (Sub-World System) – Tier-basiert.
    // tier === null  → Feature deaktiviert
    // tier === 'tier1' → 10 Instanzen
    // tier === 'tier2' → 100 Instanzen
    // multiInstanceCap wird zurückgesetzt damit der Tier-Wert effektiv wird
    // (resolveInstanceCap priorisiert sonst den expliziten Cap-Override).
    const setMultiInstanceTier = (userId, tier) => {
        const userRef = dbRef(db, `Users/${userId}`);
        const updates = {
            multiInstance: tier !== null,
            multiInstanceTier: tier,
        };
        update(userRef, updates)
            .then(() => remove(dbRef(db, `Users/${userId}/multiInstanceCap`)).catch(() => {}))
            .catch(error => {
                console.error("Error updating multiInstanceTier:", error);
            });
    };

    // Prüfen ob ein User Mitglied in anderen Teams ist (neue + alte Struktur)
    const getTeamMemberships = (userData) => {
        const memberships = [];
        // Neue Struktur: teamMemberships (plural)
        if (userData.teamMemberships) {
            for (const [teamId, data] of Object.entries(userData.teamMemberships)) {
                memberships.push({ teamId, ...data });
            }
        }
        // Legacy: teamMembership (singular)
        if (userData.teamMembership?.teamId && !memberships.some(m => m.teamId === userData.teamMembership.teamId)) {
            memberships.push(userData.teamMembership);
        }
        return memberships;
    };

    const hasTeamMemberships = (userData) => getTeamMemberships(userData).length > 0;

    // User der ein Team-Member ist (kein eigener Account)
    const isMember = (userData) => userData.accountType === 'member';

    // Nur User mit kaputtem/fehlendem accountType brauchen einen Fix (nicht legitime Members!)
    const needsOwnerFix = (userData) => userData.accountType !== 'owner' && userData.accountType !== 'member';

    // accountType auf 'owner' setzen
    const fixOwnerStatus = async (userId) => {
        try {
            const userRef = dbRef(db, `Users/${userId}`);
            await update(userRef, { accountType: 'owner' });
            console.log(`✅ Fixed accountType to 'owner' for user ${userId}`);
        } catch (error) {
            console.error("Error fixing owner status:", error);
        }
    };

    const toggleTeamDetails = (userId) => {
        setExpandedTeams((prev) => {
            const next = new Set(prev);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            return next;
        });
    };

    const copyUid = (userId) => {
        navigator.clipboard?.writeText(userId).then(() => {
            setCopiedUid(userId);
            setTimeout(() => setCopiedUid(null), 1500);
        }).catch(() => {});
    };

    const toggleSort = useCallback((key) => {
        if (sortKey === key) {
            setSortDir((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'));
            return;
        }
        setSortKey(key);
        setSortDir(SORT_COLUMNS[key].numeric ? 'desc' : 'asc');
    }, [sortKey]);

    // Live-Flags aus dem Listener + nachgeladene Zählwerte zu einer Zeile mergen.
    const rows = useMemo(() => {
        const merged = {};
        for (const [userId, user] of Object.entries(users)) {
            const membershipPlanId = resolveMembershipPlanId(userPlans[userId], user);
            const row = {
                ...user,
                ...(stats[userId] || EMPTY_STATS),
                membershipPlanId,
                researchFromPlan: {},
            };
            for (const key of RESEARCH_PLAN_KEYS) {
                const raw = user.researchRaw?.[key] ?? null;
                row[key] = resolveResearchFlag(key, raw, membershipPlanId);
                row.researchFromPlan[key] = raw === null && row[key];
            }
            merged[userId] = row;
        }
        return merged;
    }, [users, stats, userPlans]);

    const visibleUsers = useMemo(() => {
        const term = search.trim().toLowerCase();
        const statusTest = (STATUS_FILTER_MAP[statusFilter] || STATUS_FILTER_MAP.all).test;
        const [featureMode, featureKey] = featureFilter.split(':');

        const filtered = Object.entries(rows).filter(([userId, u]) => {
            if (term) {
                const haystack = `${u.email || ''} ${u.name || ''} ${userId}`.toLowerCase();
                if (!haystack.includes(term)) return false;
            }
            if (!statusTest(u, u.lastSeenAt ? now - u.lastSeenAt : Infinity)) return false;
            if (featureKey) {
                const isOn = u[featureKey] === true;
                if (featureMode === 'has' && !isOn) return false;
                if (featureMode === 'missing' && isOn) return false;
            }
            return true;
        });

        const { accessor } = SORT_COLUMNS[sortKey] || SORT_COLUMNS.lastSeen;
        const factor = sortDir === 'asc' ? 1 : -1;
        return filtered.sort(([, a], [, b]) => {
            const va = accessor(a);
            const vb = accessor(b);
            if (typeof va === 'string' || typeof vb === 'string') {
                return String(va).localeCompare(String(vb)) * factor;
            }
            return (va - vb) * factor;
        });
    }, [rows, search, statusFilter, featureFilter, sortKey, sortDir, now]);

    // Wie viele Accounts sind gerade/kürzlich aktiv — schneller Puls über alle User.
    const activitySummary = useMemo(() => {
        const summary = { total: 0, online: 0, today: 0, week: 0, dormant: 0 };
        Object.values(users).forEach((u) => {
            summary.total += 1;
            const idle = u.lastSeenAt ? now - u.lastSeenAt : Infinity;
            if (idle < RECENT_MS) summary.online += 1;
            if (idle < DAY_MS) summary.today += 1;
            if (idle < 7 * DAY_MS) summary.week += 1;
            if (idle >= 30 * DAY_MS) summary.dormant += 1;
        });
        return summary;
    }, [users, now]);

    // Zählwerte kommen erst nach dem Users-Snapshot rein.
    const renderCount = (value) => (
        value == null ? <span className="premium-toggle-pending">–</span> : value
    );

    const sortIcon = (key) => (
        sortKey === key ? (sortDir === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'
    );

    const renderSortableTh = (label, key, extraClass = '') => {
        const active = sortKey === key;
        return (
            <th
                className={`premium-toggle-th premium-toggle-th-sortable ${extraClass} ${active ? 'premium-toggle-th-sorted' : ''}`}
                onClick={() => toggleSort(key)}
                title={`Nach ${label} sortieren`}
            >
                {label}
                <i className={`fa ${sortIcon(key)} premium-toggle-sort-icon`}></i>
            </th>
        );
    };

    // Account und Last Seen teilen sich eine Spalte, also auch den Header.
    const renderSortLink = (label, key) => (
        <button
            type="button"
            className={`premium-toggle-th-sort ${sortKey === key ? 'premium-toggle-th-sort-active' : ''}`}
            onClick={() => toggleSort(key)}
            title={`Nach ${label} sortieren`}
        >
            {label}
            <i className={`fa ${sortIcon(key)} premium-toggle-sort-icon`}></i>
        </button>
    );

    const handleThumbnailClick = async (userId) => {
        const thumbnails = [];
        const userSurveysRef = dbRef(db, `surveys/${userId}`);
        const surveysSnapshot = await get(userSurveysRef);
        const surveysData = surveysSnapshot.val();

        if (surveysData) {
            for (const surveyId of Object.keys(surveysData)) {
                const uploadedThumbnails = surveysData[surveyId].uploadedThumbnails;
                if (uploadedThumbnails) {
                    for (const thumbnailData of Object.values(uploadedThumbnails)) {
                        thumbnails.push(thumbnailData.thumbnail);
                    }
                }
            }
        }

        setCurrentThumbnails(thumbnails);
        setShowModal(true);
    };

    return (
        <div className="premium-toggle-master-container">
            {showModal && (
                <div className="thumbnail-modal" onClick={() => setShowModal(false)}>
                    <div className="thumbnail-modal-inner" onClick={(e) => e.stopPropagation()}>
                        <button className="thumbnail-modal-close" onClick={() => setShowModal(false)}>
                            <i className="fa fa-times"></i> Schließen
                        </button>
                        <div className="thumbnail-grid">
                            {currentThumbnails.map((thumbnail, index) => (
                                <img key={index} src={thumbnail} alt={`Thumbnail ${index}`} />
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Aktivitäts-Puls — jede Kachel ist gleichzeitig ein Schnellfilter */}
            <div className="premium-toggle-activity-bar">
                {[
                    { key: 'online', value: activitySummary.online, label: 'Gerade aktiv', tone: 'online' },
                    { key: 'today', value: activitySummary.today, label: 'Heute aktiv', tone: '' },
                    { key: 'week', value: activitySummary.week, label: 'Letzte 7 Tage', tone: '' },
                    { key: 'dormant', value: activitySummary.dormant, label: '30+ Tage inaktiv', tone: 'dormant' },
                    { key: 'all', value: activitySummary.total, label: 'Accounts gesamt', tone: 'total' }
                ].map((card) => (
                    <button
                        key={card.key}
                        className={`premium-toggle-activity-stat ${card.tone ? `premium-toggle-activity-${card.tone}` : ''} ${statusFilter === card.key ? 'premium-toggle-activity-selected' : ''}`}
                        onClick={() => setStatusFilter(card.key)}
                    >
                        <span className="premium-toggle-activity-value">{card.value}</span>
                        <span className="premium-toggle-activity-label">{card.label}</span>
                    </button>
                ))}
            </div>

            <div className="premium-toggle-toolbar">
                <div className="premium-toggle-search">
                    <i className="fa fa-search"></i>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Email, Name oder User-ID suchen…"
                    />
                    {search && (
                        <button onClick={() => setSearch('')} title="Suche zurücksetzen">
                            <i className="fa fa-times"></i>
                        </button>
                    )}
                </div>

                <div className="premium-toggle-filter-chips">
                    {STATUS_FILTERS.map((f) => (
                        <button
                            key={f.key}
                            className={`premium-toggle-filter-chip ${statusFilter === f.key ? 'premium-toggle-filter-chip-on' : ''}`}
                            onClick={() => setStatusFilter(f.key)}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>

                <select
                    className="premium-toggle-feature-filter"
                    value={featureFilter}
                    onChange={(e) => setFeatureFilter(e.target.value)}
                    title="Nach freigeschaltetem Feature filtern"
                >
                    <option value="all">Feature: alle</option>
                    <optgroup label="Hat Feature">
                        {ALL_TOGGLES.map((f) => (
                            <option key={`has-${f.key}`} value={`has:${f.key}`}>✓ {f.title}</option>
                        ))}
                    </optgroup>
                    <optgroup label="Hat Feature nicht">
                        {ALL_TOGGLES.map((f) => (
                            <option key={`missing-${f.key}`} value={`missing:${f.key}`}>✕ {f.title}</option>
                        ))}
                    </optgroup>
                </select>

                <button
                    className="premium-toggle-refresh-btn"
                    onClick={() => loadStats(Object.keys(users), true)}
                    disabled={statsLoading}
                    title="Credits, Pages und Bilder neu einlesen"
                >
                    <i className={`fa fa-sync ${statsLoading ? 'fa-spin' : ''}`}></i>
                    {statsLoading ? 'Lädt Zahlen…' : 'Zahlen neu laden'}
                </button>

                <span className="premium-toggle-result-count">
                    <strong>{visibleUsers.length}</strong> von {activitySummary.total}
                </span>
            </div>

            <div className="premium-toggle-table-wrapper">
            <table className="premium-toggle-user-table">
                <thead className="premium-toggle-table-head">
                    <tr className="premium-toggle-header-row">
                        <th className="premium-toggle-th premium-toggle-th-account">
                            <span className="premium-toggle-th-dual">
                                {renderSortLink('Account', 'account')}
                                <span className="premium-toggle-th-sep">·</span>
                                {renderSortLink('Last Seen', 'lastSeen')}
                            </span>
                        </th>
                        <th className="premium-toggle-th premium-toggle-th-plan">Plan</th>
                        <th className="premium-toggle-th premium-toggle-th-features">Features</th>
                        {renderSortableTh('Credits', 'credits', 'premium-toggle-th-num')}
                        <th className="premium-toggle-th premium-toggle-th-spy premium-toggle-th-num">Spy</th>
                        {renderSortableTh('Pages', 'pagesCount', 'premium-toggle-th-num')}
                        {renderSortableTh('Sessions', 'sessionCount', 'premium-toggle-th-num')}
                        {renderSortableTh('Bilder', 'photoCount', 'premium-toggle-th-num')}
                        {renderSortableTh('Signup', 'signup')}
                    </tr>
                </thead>
                <tbody className="premium-toggle-table-body">
                    {visibleUsers.map(([userId, userData]) => {
                        const member = isMember(userData);
                        const memberships = getTeamMemberships(userData);
                        // Alle Worlds, in denen dieser Account freigeschaltet ist — inkl.
                        // Rolle und Berechtigungen für die aufgeklappte Ansicht.
                        const teamWorlds = memberships.length > 0
                            ? memberships.map((m) => ({
                                id: m.teamId,
                                label: m.ownerEmail || m.teamName || m.teamId,
                                subLabel: m.ownerEmail && m.teamName ? m.teamName : null,
                                role: m.isSubWorld ? 'Sub-World' : (m.memberType === 'master' ? 'Master' : 'Member'),
                                readOnly: m.readOnly === true,
                                pending: m.status === 'pending',
                                features: Array.isArray(m.features) ? m.features : []
                            }))
                            : (userData.teamOwnerEmail ? [{
                                id: 'legacy',
                                label: userData.teamOwnerEmail,
                                subLabel: null,
                                role: 'Member',
                                readOnly: false,
                                pending: false,
                                features: []
                            }] : []);
                        const teamsOpen = expandedTeams.has(userId);
                        const outreachModes = effectiveOutreachModes(
                            userData.hasOutreachJobs === true,
                            userData.outreachModes
                        );
                        return (
                        <tr key={userId} className={`premium-toggle-user-row ${hasTeamMemberships(userData) ? 'premium-toggle-team-member-row' : ''}`}>
                            {/* Account — Identität, Typ, Team-Zugehörigkeit und UID in einer Zelle */}
                            <td className="premium-toggle-td premium-toggle-td-account">
                                <div className="premium-toggle-account">
                                    <div className={`premium-toggle-avatar ${member ? 'premium-toggle-avatar-member' : ''}`}>
                                        {(userData.email || '?')[0].toUpperCase()}
                                    </div>
                                    <div className="premium-toggle-account-meta">
                                        <div className="premium-toggle-account-name">
                                            <span className="premium-toggle-account-name-text">{userData.name || 'Kein Name'}</span>
                                            {member ? (
                                                <span className="premium-toggle-badge-team">Member</span>
                                            ) : needsOwnerFix(userData) ? (
                                                <span className="premium-toggle-badge-pending">
                                                    ⚠️ {userData.accountType || 'undefined'}
                                                </span>
                                            ) : (
                                                <span className="premium-toggle-badge-owner">Owner</span>
                                            )}
                                            {!member && hasTeamMemberships(userData) && (
                                                <span className="premium-toggle-badge-team">+ Member</span>
                                            )}
                                            {!userData.hasSettings && (
                                                <span className="premium-toggle-badge-muted" title="Hat noch keine Settings angelegt">
                                                    kein Setup
                                                </span>
                                            )}
                                        </div>
                                        <div className="premium-toggle-account-email">{userData.email || '— keine Email —'}</div>
                                        <div className="premium-toggle-account-status">
                                            <div
                                                className={`premium-toggle-account-seen premium-toggle-account-seen-${lastSeenStatus(userData.lastSeenAt, now)}`}
                                                title={`Zuletzt gesehen: ${formatAbsolute(userData.lastSeenAt)}\nLogin: ${formatAbsolute(userData.lastLoginAt)}\nZugriff: ${formatAbsolute(userData.lastAccessAt)}`}
                                            >
                                                <span className="premium-toggle-seen-dot"></span>
                                                {formatSeenLabel(userData.lastSeenAt, now)}
                                            </div>
                                            {teamWorlds.length > 0 && (
                                                <button
                                                    className={`premium-toggle-team-toggle ${teamsOpen ? 'premium-toggle-team-toggle-open' : ''}`}
                                                    onClick={() => toggleTeamDetails(userId)}
                                                    title="Zeigt alle Worlds, in denen dieser Account freigeschaltet ist"
                                                >
                                                    <i className={`fa fa-chevron-${teamsOpen ? 'down' : 'right'}`}></i>
                                                    <span className="premium-toggle-team-count">
                                                        {teamWorlds.length} {teamWorlds.length === 1 ? 'World' : 'Worlds'}
                                                    </span>
                                                    {!teamsOpen && (
                                                        <span className="premium-toggle-team-preview">
                                                            {teamWorlds.slice(0, 2).map((w) => w.label).join(', ')}
                                                            {teamWorlds.length > 2 && ` +${teamWorlds.length - 2}`}
                                                        </span>
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                        {teamsOpen && teamWorlds.length > 0 && (
                                            <ul className="premium-toggle-world-list">
                                                {teamWorlds.map((world) => (
                                                    <li key={world.id} className="premium-toggle-world-item">
                                                        <div className="premium-toggle-world-head">
                                                            <span className="premium-toggle-world-name">{world.label}</span>
                                                            <span className={`premium-toggle-world-role premium-toggle-world-role-${world.role.toLowerCase().replace('-', '')}`}>
                                                                {world.role}
                                                            </span>
                                                            {world.readOnly && (
                                                                <span className="premium-toggle-world-flag">nur lesen</span>
                                                            )}
                                                            {world.pending && (
                                                                <span className="premium-toggle-world-flag premium-toggle-world-flag-pending">
                                                                    Einladung offen
                                                                </span>
                                                            )}
                                                        </div>
                                                        {world.subLabel && (
                                                            <div className="premium-toggle-world-sub">{world.subLabel}</div>
                                                        )}
                                                        {world.features.length > 0 && (
                                                            <div className="premium-toggle-world-features">
                                                                {world.features.map((f) => (
                                                                    <span key={f} className="premium-toggle-world-feature">
                                                                        {MEMBER_FEATURE_LABELS[f] || f}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}

                                        <div className="premium-toggle-account-actions">
                                            <button
                                                className="premium-toggle-uid-btn"
                                                onClick={() => copyUid(userId)}
                                                title={`User-ID kopieren: ${userId}`}
                                            >
                                                <i className={`fa ${copiedUid === userId ? 'fa-check' : 'fa-fingerprint'}`}></i>
                                                {copiedUid === userId ? 'kopiert' : `${userId.slice(0, 8)}…`}
                                            </button>
                                            {(member || needsOwnerFix(userData)) && (
                                                <button
                                                    onClick={() => fixOwnerStatus(userId)}
                                                    className="premium-toggle-btn premium-toggle-btn-fix"
                                                    title={member
                                                        ? 'Zum Owner befördern (Team-Mitgliedschaften bleiben)'
                                                        : "accountType auf 'owner' setzen"}
                                                >
                                                    → Owner
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </td>

                            {/* Plan — read-only membership. Starter/Pro/Enterprise only change in Memberships. */}
                            <td className="premium-toggle-td premium-toggle-td-plan">
                                {member ? (
                                    <span className="premium-toggle-inherited">erbt vom Owner</span>
                                ) : (
                                    <div className="premium-toggle-plan-stack">
                                        <div className="premium-toggle-plan-field">
                                            <span className="premium-toggle-plan-label">Membership</span>
                                            {userData.membershipPlanId && CREDIT_PLANS[userData.membershipPlanId] ? (
                                                <span
                                                    className={`premium-toggle-membership-badge premium-toggle-membership-badge-${userData.membershipPlanId}`}
                                                    title="Nur über den Tab Memberships änderbar"
                                                >
                                                    {CREDIT_PLANS[userData.membershipPlanId].label}
                                                    <span className="premium-toggle-membership-meta">
                                                        {CREDIT_PLANS[userData.membershipPlanId].features.emailsPerDay}/Tag
                                                        {' · '}
                                                        {CREDIT_PLANS[userData.membershipPlanId].monthlyCredits.toLocaleString()} cr
                                                    </span>
                                                </span>
                                            ) : (
                                                <span
                                                    className="premium-toggle-membership-badge premium-toggle-membership-badge-none"
                                                    title={plansLoading ? 'Memberships werden geladen' : 'Im Tab Memberships aktivieren'}
                                                >
                                                    {plansLoading ? 'Laden…' : 'Kein Plan'}
                                                </span>
                                            )}
                                        </div>
                                        {userData.parentOwnerUid ? (
                                            <div className="premium-toggle-plan-field">
                                                <span className="premium-toggle-plan-label">Instanzen</span>
                                                <span className="premium-toggle-inherited" title="Dieser Account ist eine Sub-Instanz">
                                                    Sub-Instanz
                                                </span>
                                            </div>
                                        ) : (
                                            <div className="premium-toggle-plan-field">
                                                <span className="premium-toggle-plan-label">Instanzen</span>
                                                <select
                                                    value={userData.multiInstanceTier || ''}
                                                    onChange={(e) => setMultiInstanceTier(userId, e.target.value || null)}
                                                    className={`premium-toggle-email-plan-select premium-toggle-tier-${userData.multiInstanceTier || 'off'}`}
                                                    title={`Multi-Instance — effektives Limit: ${userData.multiInstanceCap}`}
                                                >
                                                    {INSTANCE_TIER_OPTIONS.map(opt => (
                                                        <option key={opt.value || 'off'} value={opt.value || ''}>
                                                            {opt.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </td>

                            {/* Features — Produkt-Chips, danach Research / Outreach / Academy als Gruppen */}
                            <td className="premium-toggle-td premium-toggle-td-features">
                                {member ? (
                                    <span className="premium-toggle-inherited">erbt vom Owner</span>
                                ) : (
                                    <div className="premium-toggle-feature-groups">
                                        <div className="premium-toggle-chip-grid">
                                            {FEATURE_TOGGLES.map((f) => {
                                                const on = userData[f.key] === true;
                                                return (
                                                    <button
                                                        key={f.key}
                                                        onClick={() => toggleFeature(userId, f.key)}
                                                        className={`premium-toggle-chip premium-toggle-chip-${f.tone} ${on ? 'premium-toggle-chip-on' : ''}`}
                                                        title={`${f.title} ${on ? 'deaktivieren' : 'aktivieren'}`}
                                                        aria-pressed={on}
                                                    >
                                                        <i className={`fa ${f.icon}`}></i>
                                                        <span>{f.label}</span>
                                                    </button>
                                                );
                                            })}
                                            {OUTREACH_MODES.map((mode) => {
                                                const on = outreachModes[mode.id] === true;
                                                return (
                                                    <button
                                                        key={mode.id}
                                                        onClick={() => toggleOutreachMode(userId, mode.id)}
                                                        className={`premium-toggle-chip premium-toggle-chip-${mode.tone} ${on ? 'premium-toggle-chip-on' : ''}`}
                                                        title={`${mode.name} ${on ? 'ausschalten' : 'anschalten'} — ${mode.description}`}
                                                        aria-pressed={on}
                                                    >
                                                        <i className={`fa ${mode.icon}`}></i>
                                                        <span>{mode.short}</span>
                                                    </button>
                                                );
                                            })}
                                            {/* Kill-Switch: an = Inbox wird abgerufen, sobald ein Mail-Konto verbunden ist */}
                                            <button
                                                onClick={() => toggleInboxFetching(userId)}
                                                className={`premium-toggle-chip premium-toggle-chip-sky ${!userData.inboxDisabled ? 'premium-toggle-chip-on' : ''}`}
                                                title={userData.inboxDisabled
                                                    ? 'E-Mail-Abruf wieder freigeben'
                                                    : 'E-Mail-Abruf für diesen Account stoppen'}
                                                aria-pressed={!userData.inboxDisabled}
                                            >
                                                <i className="fa fa-inbox"></i>
                                                <span>Inbox</span>
                                            </button>
                                        </div>

                                        <div className="premium-toggle-feature-group">
                                            <button
                                                className="premium-toggle-group-label"
                                                onClick={() => toggleAllResearch(userId)}
                                                title="Alle Research-Tools auf einmal an- oder abschalten"
                                            >
                                                Research
                                                <span className="premium-toggle-research-count">
                                                    {RESEARCH_TOGGLES.filter(f => userData[f.key] === true).length}/{RESEARCH_TOGGLES.length}
                                                </span>
                                            </button>
                                            {RESEARCH_TOGGLES.map((f) => {
                                                const on = userData[f.key] === true;
                                                const fromPlan = userData.researchFromPlan?.[f.key] === true;
                                                return (
                                                    <button
                                                        key={f.key}
                                                        onClick={() => toggleResearch(userId, f.key)}
                                                        className={`premium-toggle-chip premium-toggle-chip-${f.tone} ${on ? 'premium-toggle-chip-on' : ''} ${fromPlan ? 'premium-toggle-chip-plan' : ''}`}
                                                        title={fromPlan
                                                            ? `${f.title}: aktiv über den ${CREDIT_PLANS[userData.membershipPlanId]?.label || ''}-Plan — klicken zum Deaktivieren`
                                                            : `${f.title} ${on ? 'deaktivieren' : 'aktivieren'}`}
                                                        aria-pressed={on}
                                                    >
                                                        <i className={`fa ${f.icon}`}></i>
                                                        <span>{f.label}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {userData.hasAcademy && (
                                            <div className="premium-toggle-feature-group">
                                                <span className="premium-toggle-group-label">Academy</span>
                                                {ACADEMY_TIERS.map(tier => (
                                                    <button
                                                        key={tier.key}
                                                        onClick={() => toggleAcademyTier(userId, tier.key)}
                                                        className={`premium-toggle-chip premium-toggle-chip-blue ${userData.academyTiers?.[tier.key] ? 'premium-toggle-chip-on' : ''}`}
                                                        title={tier.title}
                                                    >
                                                        <span>{tier.label}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </td>

                            <td className="premium-toggle-td premium-toggle-td-count">{renderCount(userData.credits)}</td>
                            <td className="premium-toggle-td premium-toggle-td-spy-credits">
                                <button 
                                    className="premium-toggle-spy-credits-btn"
                                    onClick={() => openSpyCreditsModal(userId, userData)}
                                    title={`Monthly ${userData.spyCredits ?? 0} (resets). Extra ${userData.bonusCredits ?? 0} (kept). Click to add extra credits.`}
                                >
                                    <span className="spy-credits-value">{Math.round(((Number(userData.spyCredits) || 0) + (Number(userData.bonusCredits) || 0)) * 100) / 100}</span>
                                    <span className="spy-credits-add">+</span>
                                </button>
                            </td>
                            <td className="premium-toggle-td premium-toggle-td-count">{renderCount(userData.pagesCount)}</td>
                            <td className="premium-toggle-td premium-toggle-td-count">{userData.sessionCount}</td>
                            <td className="premium-toggle-td premium-toggle-td-count">
                                {userData.photoCount == null ? (
                                    <span className="premium-toggle-pending">–</span>
                                ) : userData.photoCount > 0 ? (
                                    <button
                                        className="premium-toggle-photo-btn"
                                        onClick={() => handleThumbnailClick(userId)}
                                        title="Hochgeladene Bilder ansehen"
                                    >
                                        {userData.photoCount}
                                    </button>
                                ) : (
                                    <span className="premium-toggle-zero">0</span>
                                )}
                            </td>
                            <td className="premium-toggle-td premium-toggle-td-date">
                                {formatShortDate(toMillis(userData.signupTimestamp))}
                            </td>
                        </tr>
                        );
                    })}
                </tbody>
            </table>

            {visibleUsers.length === 0 && (
                <div className="premium-toggle-empty">
                    <i className="fa fa-user-slash"></i>
                    <p>Kein Account passt zu Suche und Filter.</p>
                    <button onClick={() => { setSearch(''); setStatusFilter('all'); setFeatureFilter('all'); }}>
                        Filter zurücksetzen
                    </button>
                </div>
            )}
            </div>

            {/* Spy Credits Modal */}
            {showCreditsModal && selectedUserForCredits && (
                <div className="spy-credits-modal-overlay" onClick={() => setShowCreditsModal(false)}>
                    <div className="spy-credits-modal" onClick={e => e.stopPropagation()}>
                        <div className="spy-credits-modal-header">
                            <h3>Add extra credits</h3>
                            <button className="spy-credits-modal-close" onClick={() => setShowCreditsModal(false)}>×</button>
                        </div>
                        <div className="spy-credits-modal-body">
                            <div className="spy-credits-user-info">
                                <span className="spy-credits-email">{selectedUserForCredits.email}</span>
                                <span className="spy-credits-current">
                                    Monthly: <strong>{stats[selectedUserForCredits.id]?.spyCredits || 0}</strong> (resets)
                                    {' · '}
                                    Extra: <strong>{stats[selectedUserForCredits.id]?.bonusCredits || 0}</strong> (kept)
                                </span>
                            </div>
                            <div className="spy-credits-input-group">
                                <label>Extra credits to add</label>
                                <input
                                    type="number"
                                    value={creditsToAdd}
                                    onChange={(e) => setCreditsToAdd(e.target.value)}
                                    placeholder="Enter amount"
                                    min="1"
                                />
                            </div>
                            <div className="spy-credits-quick-btns">
                                {[50, 100, 250, 500, 1000, 10000].map(amt => (
                                    <button key={amt} onClick={() => setCreditsToAdd(amt.toString())}>+{amt}</button>
                                ))}
                            </div>
                            {creditsToAdd && parseFloat(creditsToAdd) > 0 && (
                                <div className="spy-credits-preview">
                                    Spendable after: <strong>{(stats[selectedUserForCredits.id]?.spyCredits || 0) + (stats[selectedUserForCredits.id]?.bonusCredits || 0) + parseFloat(creditsToAdd)}</strong>
                                    <div>The extra credits stay after the monthly reset.</div>
                                </div>
                            )}
                        </div>
                        <div className="spy-credits-modal-footer">
                            <button className="spy-credits-cancel-btn" onClick={() => setShowCreditsModal(false)}>Cancel</button>
                            <button 
                                className="spy-credits-submit-btn"
                                onClick={handleAddSpyCredits}
                                disabled={isAddingCredits || !creditsToAdd || parseFloat(creditsToAdd) <= 0}
                            >
                                {isAddingCredits ? 'Adding...' : `Add ${creditsToAdd || 0} Credits`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PremiumToggle;
